// The Ask Opus and agent orb (ported from Alex's experiments; typed in
// orb.d.ts): one soft shape with eyes that carries the agent's
// state itself. Five states, each a short loop; moving from one to the next
// leaves an afterimage of the form it had. The orb is never swapped for
// another shape: it shrinks into the first of the thinking dots, or into the
// dot of the exclamation mark, and grows back out of it.
import {
  m4,
  rad,
  seg,
  smooth,
  mix,
  clamp,
  easeOut,
  easeInOut,
  ellipse,
  rrect,
  roundPoly,
  pillow,
  ribbon,
  fan,
} from "./engine";

const TAU = 2 * Math.PI;
const DOT_Y = -0.52; // where the orb sits when it shrinks to the dot of the "!"
const DOT = 0.28; // how small it gets as the first of the thinking dots

// ---------- the parts ----------
const head = pillow(ellipse(0.72), { T: 0.2, a: 2, p: 2.1 });
const eye = pillow(rrect(0.15, 0.34, 0.075), { T: 0.03, a: 3, p: 2 });
const smile = ribbon(
  Array.from({ length: 13 }, (_, i) => {
    const a = rad(165 - (150 * i) / 12);
    return [0.115 * Math.cos(a), 0.115 * Math.sin(a) - 0.05];
  }),
  0.08,
  { T: 0.03 },
);
// Done's eyes: a checkmark, the smile's stroke in two straight legs.
const CHECK = [
  [-0.17, 0.0],
  [-0.06, -0.11],
  [0.18, 0.13],
];
const check = ribbon(
  CHECK.slice(1).flatMap(([x1, y1], i) => {
    const [x0, y0] = CHECK[i];
    return Array.from({ length: 6 }, (_, j) => {
      const k = (j + (i ? 1 : 0)) / 6;
      return [mix(x0, x1, k), mix(y0, y1, k)];
    });
  }),
  // Bold: about one and a half times the smile's stroke.
  0.13,
  { T: 0.04 },
);
const bar = pillow(
  roundPoly(
    [
      [-0.1, 0.26],
      [0.1, 0.26],
      [0.17, 1.24],
      [-0.17, 1.24],
    ],
    [0.1, 0.1, 0.17, 0.17],
  ),
  { T: 0.12, a: 2, p: 1.5 },
);

const tilt = (P) => m4.mul(m4.T(0, DOT_Y, 0), m4.RZ(P.br), m4.T(0, -DOT_Y, 0)); // the "!" rocks on its dot
// k places behind the leader. td blends a flat circle (four dots, a quarter
// turn apart) into a tilted ring seen in depth (three dots, a third apart):
// near the viewer a dot comes forward and grows, on the far side it shrinks.
const orbit3 = (P, k) => {
  const flatA = P.oa + (k * P.sp * TAU) / 4,
    deepA = P.oa + (k * P.sp * TAU) / 3;
  const fx = P.ro * Math.cos(flatA),
    fy = P.ro * Math.sin(flatA);
  const near = Math.sin(deepA); // +1 nearest the viewer
  const dx = P.ro * 1.15 * Math.cos(deepA),
    dy = -P.ro * 0.3 * near,
    dz = P.ro * 0.9 * near;
  return {
    x: mix(fx, dx, P.td),
    y: mix(fy, dy, P.td),
    z: dz * P.td,
    s: 1 + P.td * 0.38 * near,
  };
};
const headM = (P) => {
  const o = orbit3(P, 0),
    hs = P.hs * o.s;
  return m4.mul(
    tilt(P),
    m4.T(P.hx + o.x, P.hy + o.y, o.z),
    m4.RZ(P.hr),
    m4.S(hs, hs * P.sq, hs),
  );
};
// The eyes ride a sphere: longitude lon turns them round the ball, squeezing
// toward the edge and hiding while they are round the back.
const EYE_R = 0.5;
const eyeM = (P, side, k, open, face = FACE) => {
  const phi =
    Math.asin(clamp((side * 0.21 * face.scale + P.gx) / EYE_R, -1, 1)) + P.lon;
  const front = Math.cos(phi),
    seen = clamp((front - 0.02) / 0.2),
    ks = k * face.scale;
  return m4.mul(
    headM(P),
    m4.T(EYE_R * Math.sin(phi) + face.dx, 0.05 + face.dy + P.gy, 0.215),
    m4.S(ks * Math.max(0.05, front) * seen, ks * open * seen, 1),
  );
};
const FACE = { dx: 0, dy: 0, scale: 1 };

// One orb, built round any outline: the head (and the thinking dots) are that
// outline puffed into a soft solid; the face sits where the outline wants it.
export function makeOrb(
  headMesh = head,
  face = FACE,
  id = "orb",
  discR = 0.72,
) {
  return {
    id,
    name: id,
    grad: [-0.62, 0.5],
    radius: 0.9,
    root: (P) =>
      m4.mul(m4.T(0, P.oy, 0), m4.RX(rad(4) + P.rx), m4.RY(P.ry), m4.S(1.25)),
    parts: [
      { kind: "head", mesh: headMesh, discR, pose: (P) => ({ m: headM(P) }) },
      // No eyes when the face says so (the dots, "!" and check still show).
      ...(face.eyes === false ? [] : [-1, 1]).flatMap((side) => [
        {
          kind: "eye",
          mesh: eye,
          tone: 0.95,
          pose: (P) => ({ m: eyeM(P, side, P.es * (1 - P.eh), P.eb, face) }),
        },
        {
          kind: "eye",
          mesh: smile,
          tone: 0.95,
          pose: (P) => ({ m: eyeM(P, side, P.es * P.eh, 1, face) }),
        },
      ]),
      {
        kind: "eye",
        mesh: check,
        tone: 0.95,
        pose: (P) => ({
          m: m4.mul(headM(P), m4.T(0, 0.04, 0.22), m4.S(P.ck)),
        }),
      },
      // The dots that follow the leader round once it is a dot itself.
      ...[1, 2, 3].map((k) => ({
        kind: "dot",
        mesh: headMesh,
        discR,
        pose: (P) => {
          const o = orbit3(P, k),
            hide = k === 3 ? 1 - P.td : 1;
          return {
            m: m4.mul(m4.T(o.x, o.y, o.z), m4.S(DOT * P.ds * o.s * hide)),
          };
        },
      })),
      {
        kind: "bar",
        mesh: bar,
        pose: (P) => ({ m: m4.mul(tilt(P), m4.T(0, DOT_Y, 0), m4.S(P.bs)) }),
      },
    ],
  };
}
export const orb = makeOrb();
/** The same orb with no eyes: the agents' textured orbs. */
export const orbNoEyes = makeOrb(
  undefined,
  { ...FACE, eyes: false },
  "orb-no-eyes",
);
// A bot from an outline: [[x, y], ...] in head units (the round orb is radius .72).
// The gradient is fitted to the outline: its edge sits between the outline's
// average and furthest reach, so long shapes don't go solid gold at the ends.
export const makeBot = (outline, face, id) => {
  const r = outline.map(([x, y]) => Math.hypot(x, y)),
    mean = r.reduce((a, b) => a + b, 0) / r.length;
  return makeOrb(
    pillow(outline, { T: 0.2, a: 2, p: 2.1 }),
    face,
    id,
    (mean + Math.max(...r)) / 2,
  );
};

// ---------- the numbers ----------
// hs, hx, hy, hr, sq  the head: scale, place, roll, squash
// oy, rx, ry          the whole orb: lift and lean
// gx, gy              where the eyes look
// es, eb, eh          eyes: size, how open, how much they smile
// ro, oa, sp, ds      the orbit: its radius, the leader's angle, how far the
//                     followers have dropped behind (0 = inside the leader), their size
// bs, br              the bar of the "!": size, and how far the "!" rocks
// ck                  done's checkmark, in place of the eyes: its size
const BASE = {
  td: 0,
  lon: 0,
  wh: 0,
  wa: 0,
  hs: 1,
  hx: 0,
  hy: 0,
  hr: 0,
  sq: 1,
  oy: 0,
  rx: 0,
  ry: 0,
  gx: 0,
  gy: 0,
  es: 1,
  eb: 1,
  eh: 0,
  ro: 0,
  oa: 0,
  sp: 0,
  ds: 0,
  bs: 0,
  br: 0,
  ck: 0,
};

// A value that holds, then eases to the next: [when, value, how long the move takes].
const track = (t, keys) =>
  keys
    .slice(1)
    .reduce(
      (v, [t0, to, d = 0.18]) => mix(v, to, easeInOut(seg(t, t0, t0 + d), 2)),
      keys[0][1],
    );
const blink = (t, t0) =>
  smooth(t, t0, t0 + 0.06) * (1 - smooth(t, t0 + 0.07, t0 + 0.17));
const hop = (t, t0) =>
  easeOut(seg(t, t0, t0 + 0.15), 2) *
  (1 - easeInOut(seg(t, t0 + 0.15, t0 + 0.5), 2));
const back = (x) => 1 + 2.3 * Math.pow(x - 1, 3) + 1.3 * Math.pow(x - 1, 2); // lands a touch past, then settles

const HEAD = (p) => p.kind !== "eye",
  EYES = (p) => p.kind === "eye",
  ALL = () => true;

export const STATES = {
  // Calm and a little curious: breathes, looks about, blinks.
  idle: {
    name: "Idle",
    dur: 6.4,
    params(t) {
      const b = Math.sin((t / 3.2) * TAU);
      const gx = track(t, [
        [0, 0],
        [1.3, 0.17],
        [2.9, 0],
        [4.1, -0.18],
        [5.5, 0],
      ]);
      const gy = track(t, [
        [0, 0],
        [1.3, 0.07],
        [2.9, 0],
        [4.1, 0.02],
        [5.5, 0],
      ]);
      return {
        ...BASE,
        hs: 1 + 0.012 * b,
        sq: 1 - 0.01 * b,
        hy: 0.012 * b,
        gx,
        gy,
        ry: gx * 1.1,
        rx: -gy,
        hr: track(t, [
          [0, 0],
          [1.3, -0.07, 0.3],
          [2.9, 0, 0.3],
          [4.1, 0.06, 0.3],
          [5.5, 0, 0.3],
        ]),
        eb:
          1 -
          0.92 *
            Math.min(
              1,
              blink(t, 1.2) + blink(t, 3.95) + blink(t, 5.4) + blink(t, 5.75),
            ),
      };
    },
  },
  // Three dots in depth: the orb shrinks into the first, two more peel off
  // it, and the three run round a tilted ring, coming forward and growing on
  // the near side and passing behind, smaller, on the far side.
  thinking: {
    name: "Thinking",
    dur: Infinity,
    hold: 0.62,
    stillAt: 1.1,
    moving: HEAD,
    fan: fan(6, [0.12, 0.9]),
    params(t) {
      const w = TAU / 1.5,
        turn = w * t - 0.22 * Math.sin(2 * w * t);
      return {
        ...BASE,
        td: 1,
        es: 0,
        hs: DOT,
        ds: 1,
        ro: 0.52 * easeOut(seg(t, 0, 0.45), 2.2),
        oa: TAU / 4 - turn,
        sp: easeInOut(seg(t, 0.1, 0.8), 2),
      };
    },
  },
  // The orb is the dot of an exclamation mark. The bar rises out of it, and
  // from then on the mark rocks for your attention.
  needs: {
    name: "Needs you",
    dur: 2.8,
    intro: 0.9,
    hold: 0.62,
    stillAt: 0.9,
    introTrail: { on: 0, off: 0.16, delay: 0.1 },
    trail: { on: 0.12, off: 0.95, delay: 0.16 },
    moving: HEAD,
    params(t) {
      const l = t - 0.9; // time in the loop, once the bar is up
      const br =
        rad(17) *
        Math.sin(TAU * 2.6 * (l - 0.12)) *
        smooth(l, 0.12, 0.2) *
        (1 - smooth(l, 0.55, 1.15));
      return {
        ...BASE,
        es: 0,
        hs: 0.2,
        hy: DOT_Y,
        bs: back(seg(t, 0, 0.34)),
        br,
        oy: 0.06 * hop(l, 0.12),
      };
    },
  },
  // Spin: the bot-avatars flip. It squats, hops, turns once round its own
  // vertical axis while a ribbon whirls round it, and lands with a squash.
  // wh/wa drive the ribbon (strength, angle), drawn in 2D by the page.
  whirl: {
    name: "Spin",
    dur: 1.9,
    hold: 0.4,
    stillAt: 0.7,
    // No afterimages: the ribbon carries the motion, and a ghost of the
    // resting eyes would sit on the face while they are round the back.
    params(t) {
      const ss = (a, b, x) => {
        const y = clamp((x - a) / (b - a));
        return y * y * (3 - 2 * y);
      };
      const cubic = (x) =>
        x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
      const PRE = 0.16,
        AIR = 0.68,
        LAND = 0.3;
      const Y = clamp((t - PRE) / AIR),
        arc = Math.sin(Math.PI * Y);
      const crouch = ss(0, PRE, t) * (1 - ss(PRE, PRE + 0.06, t));
      const l = t - PRE - AIR,
        land =
          l > 0 && l < LAND
            ? Math.sin((Math.PI * l) / LAND) * Math.exp((-2.2 * l) / LAND)
            : 0;
      const squash = 0.14 * crouch + 0.16 * land - 0.07 * arc;
      return {
        ...BASE,
        oy: 0.26 * arc - 0.03 * crouch,
        lon: TAU * cubic(Y),
        hr: 0.06 * arc,
        hs: 1,
        sq: 1 - squash,
        eb: 1 - 0.5 * ss(0.1, 0.26, Y) * (1 - ss(0.66, 0.9, Y)),
        wh: ss(0.1, 0.26, Y) * (1 - ss(0.66, 0.9, Y)),
        wa: TAU * (1.5 * Y + 0.9 * cubic(Y)),
      };
    },
  },
};
// ---------- working and done: the dots ----------
// The orb is the leader of four dots on a flat ring (td 0, a quarter turn
// apart at sp 1), each the leader's size (ds 1), eyes hidden. Working runs
// the ring; done brings the dots home into the orb, which comes back
// with a checkmark for eyes.
const RING = { ...BASE, td: 0, es: 0, hs: DOT, ds: 1, ro: 0.5, sp: 1 };
const START = TAU / 4; // the leader starts at the top

// Four even dots turning at a steady pace.
STATES.working = {
  name: "Working",
  dur: 1.2,
  hold: 0.5,
  stillAt: 0.3,
  params: (t) => ({ ...RING, oa: START - (TAU * t) / 1.2 }),
};

// The ring winds in, the dots fold into the leader, and the orb grows back
// out of it with a checkmark for eyes and a small hop; then it rests,
// breathing (the loop).
const DONE_INTRO = 1.3;
STATES.done = {
  name: "Done",
  intro: DONE_INTRO,
  dur: 6.4,
  hold: 0.3,
  stillAt: DONE_INTRO + 0.4,
  params(t) {
    if (t >= DONE_INTRO) {
      const b = Math.sin(((t - DONE_INTRO) / 3.2) * TAU);
      return {
        ...BASE,
        es: 0,
        ck: 1,
        hs: 1 + 0.012 * b,
        sq: 1 - 0.01 * b,
        hy: 0.012 * b,
      };
    }
    const k = easeInOut(seg(t, 0, 0.55), 2),
      grow = back(seg(t, 0.4, 0.85)),
      lift = hop(t, 0.8);
    return {
      ...RING,
      ro: 0.5 * (1 - k),
      sp: 1 - k,
      ds: 1 - k,
      oa: START - TAU * 0.6 * easeOut(seg(t, 0, 0.55), 2),
      hs: mix(DOT, 1, grow),
      ck: back(seg(t, 0.6, 0.95)),
      oy: 0.06 * lift,
      sq: 1 - 0.05 * lift,
    };
  },
};

// rest: the form a state is entered in. loopRest: the form its loop starts from.
for (const s of Object.values(STATES)) {
  s.rest = s.params(0);
  s.loopRest = s.params(s.intro || 0);
}

// ---------- moving between states ----------
const SWITCH = 0.42,
  LEAVE = 0.12,
  HOLD = 0.8; // the change, when its afterimage starts to go, and when the state's own motion begins
const holdOf = (c) => c.hold ?? HOLD;
// Seconds since the state's own motion began -> where that is in its intro or loop.
const clipTime = (c, u) => {
  const i = c.intro || 0;
  return u < i ? u : i + ((u - i) % c.dur);
};
const blend = (a, b, k) => {
  const P = {};
  for (const key in a) P[key] = mix(a[key], b[key], k);
  for (const key of ["es", "eb", "eh", "ds", "bs", "ro", "sp"])
    P[key] = clamp(P[key]);
  return P;
};
const NONE = { since: -1, gone: -1 };
const AFTERIMAGES = false;

export class Agent {
  constructor(state = "idle", still = false) {
    this.state = state;
    this.clip = STATES[state];
    this.from = null;
    this.t0 = null;
    this.still = still;
  }
  // Switch state at time `now` (seconds). The form it has at that instant becomes the ghost.
  set(state, now) {
    if (state === this.state) return;
    const from = this.numbers(now);
    from.oa = Math.atan2(Math.sin(from.oa), Math.cos(from.oa)); // however many turns it has made, unwind less than one
    from.lon = Math.atan2(Math.sin(from.lon), Math.cos(from.lon));
    this.change(from, state, now);
  }
  change(from, state, now) {
    const to = STATES[state].rest;
    // If the outline changes the whole orb leaves a ghost; if not, only the eyes do.
    this.reshaped =
      Math.abs(from.hs - to.hs) > 0.15 ||
      Math.abs(from.bs - to.bs) > 0.3 ||
      Math.abs(from.ds - to.ds) > 0.3;
    this.from = from;
    this.state = state;
    this.clip = STATES[state];
    this.t0 = now;
  }
  // Hold a known moment, for checking: `since` seconds into the state's own
  // motion, or into the change to it from the resting form of `from`.
  hold(now, state, since, from) {
    if (from)
      return this.change(
        STATES[from].params(STATES[from].stillAt ?? 0),
        state,
        now - since,
      );
    this.from = null;
    this.state = state;
    this.clip = STATES[state];
    this.t0 = now - since - holdOf(this.clip);
  }
  numbers(time) {
    const c = this.clip,
      since = time - this.t0;
    if (this.from && since < 0) return this.from;
    if (this.from && since < holdOf(c))
      return blend(this.from, c.rest, back(seg(since, 0, SWITCH)));
    return c.params(
      this.still ? c.stillAt ?? 0 : clipTime(c, since - holdOf(c)),
    );
  }
  // Everything the renderer needs for this instant.
  pose(now) {
    const c = this.clip;
    if (this.t0 === null) this.t0 = now - holdOf(c);
    const since = now - this.t0,
      P = this.numbers(now);
    // No afterimages: every state is just the orb (or its dots), in its own
    // colours. The trail and ghost machinery below stays for when they return.
    if (!AFTERIMAGES) return { P, rest: P, past: P, ...NONE };
    if (this.from && since < holdOf(c)) {
      return {
        P,
        rest: this.from,
        past: this.numbers(now - 0.14),
        since,
        gone: since - LEAVE,
        // When the outline changes the ghost goes behind, so the orb itself stays in view
        // as it shrinks into a dot or grows back over what it was.
        moving: this.reshaped ? ALL : EYES,
        under: this.reshaped,
      };
    }
    const u = since - holdOf(c),
      i = c.intro || 0,
      t = clipTime(c, u);
    const at = (x) => c.params(Math.max(0, x));
    if (this.still) return { P, rest: P, past: P, ...NONE };
    if (u < i) {
      const tr = c.introTrail;
      return tr
        ? {
            P,
            rest: c.rest,
            past: at(t - tr.delay),
            since: t - tr.on,
            gone: t - tr.off,
            moving: c.moving,
            fan: c.fan,
          }
        : { P, rest: P, past: P, ...NONE };
    }
    const tr = c.trail;
    if (!tr) return { P, rest: P, past: P, ...NONE };
    if (tr.comet) {
      // A tail rather than a ghost: it runs from where the dots were a moment ago, thinning as it goes.
      const tail = at(t - tr.comet);
      return {
        P,
        rest: { ...tail, hs: tail.hs * 0.45, ds: tail.ds * 0.45 },
        past: at(t - tr.delay),
        since: t - tr.on,
        gone: -1,
        moving: c.moving,
        fan: c.fan,
        under: true,
      };
    }
    const l = t - i;
    return {
      P,
      rest: c.loopRest,
      past: c.params(i + Math.max(0, l - tr.delay)),
      since: l - tr.on,
      gone: l - tr.off,
      moving: c.moving,
      fan: c.fan,
    };
  }
}
